import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useLang } from "../contexts/LanguageContext";
import { useAuth, formatApiError } from "../contexts/AuthContext";
import { QrCode, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { t } = useLang();
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(form.email, form.password, form.name);
      toast.success("Account created");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-purple-50/40 to-white text-zinc-900 dark:bg-gradient-to-b dark:from-zinc-950 dark:via-purple-950/30 dark:to-zinc-950 dark:text-zinc-100 relative overflow-hidden" data-testid="register-page" style={{ marginTop: "-4rem", paddingTop: "4rem" }}>
      <div className="absolute -top-40 right-1/4 w-[500px] h-[500px] rounded-full bg-fuchsia-600/15 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-purple-600/10 blur-[120px] pointer-events-none" />
      <div className="grid lg:grid-cols-2 min-h-[calc(100vh-4rem)] relative">
        <div className="grid place-items-center p-8 order-2 lg:order-1">
          <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="register-form">
            <div>
              <div className="w-12 h-12 rounded-2xl bg-purple-600 grid place-items-center text-white shadow-lg shadow-purple-600/40 mb-5">
                <QrCode className="h-6 w-6" />
              </div>
              <h1 className="text-3xl font-bold tracking-tight">{t("auth.registerTitle")}</h1>
              <p className="text-zinc-500 dark:text-zinc-400 mt-1 text-sm">{t("auth.registerSubtitle")}</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-zinc-700 dark:text-zinc-300">{t("auth.name")}</Label>
              <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="register-name" className="rounded-xl h-11" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-zinc-700 dark:text-zinc-300">{t("auth.email")}</Label>
              <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="register-email" className="rounded-xl h-11" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-zinc-700 dark:text-zinc-300">{t("auth.password")}</Label>
              <Input id="password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="register-password" className="rounded-xl h-11" />
            </div>
            <Button type="submit" disabled={busy} className="w-full rounded-2xl h-11 bg-purple-600 hover:bg-purple-700 text-white shadow-lg shadow-purple-600/30" data-testid="register-submit">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("auth.signUp")}
            </Button>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 text-center">
              {t("auth.hasAccount")}{" "}
              <Link to="/login" className="text-purple-600 dark:text-purple-400 font-semibold hover:underline" data-testid="register-to-login">
                {t("auth.signIn")}
              </Link>
            </p>
          </form>
        </div>
        <div className="hidden lg:block relative order-1 lg:order-2">
          <div className="absolute inset-6 rounded-3xl bg-gradient-to-br from-fuchsia-700 via-purple-800 to-zinc-900 overflow-hidden border border-purple-500/20">
            <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(circle at 70% 80%, rgba(232,121,249,0.4), transparent 50%)" }} />
            <div className="relative h-full grid place-items-center p-12 text-white">
              <div className="max-w-sm">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/20 bg-white/5 text-xs font-medium mb-4">
                  <Sparkles className="w-3 h-3" />
                  Welcome
                </div>
                <p className="text-3xl font-bold leading-tight">{t("landing.ctaTitle")}</p>
                <p className="mt-4 text-white/70">{t("landing.ctaDesc")}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
